import React, { useState, useEffect, useRef } from "react";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import ContextSelector from "../ContextSelector";
import { useWindowSize } from "@shared/hooks/useWindowSize";
import { usePartnerCustomerSelection } from "../usePartnerCustomerSelection";

interface PartnerCustomerDropdownProps {
  tenantType: string; // "CUSTOMER" | "PARTNER" | "GLOBAL_ADMIN" | ...
  isLoading: boolean;
  isAuthenticated: boolean;
  getAccessTokenSilently: () => Promise<string>;
}

/**
 * Top-bar partner/customer selectors. All data + behavior lives in
 * `usePartnerCustomerSelection` (shared with the logo card); this component is
 * just the top-bar presentation.
 */
export default function PartnerCustomerDropdown({
  tenantType,
  isLoading,
  isAuthenticated,
  getAccessTokenSilently,
}: PartnerCustomerDropdownProps) {
  const {
    isFetching,
    partnerOptions,
    partnerName,
    onSelectPartner,
    customerOptions,
    customerDisplay,
    onSelectCustomer,
    rightLabel,
    rightId,
  } = usePartnerCustomerSelection({
    tenantType,
    isLoading,
    isAuthenticated,
    getAccessTokenSilently,
  });

  // Mobile detection
  const { width } = useWindowSize();
  const [isMobile, setIsMobile] = useState(() => width <= 1024);
  const prevIsMobileRef = useRef(isMobile);
  useEffect(() => {
    const newIsMobile = width <= 1024;
    // Only update state if it actually changed (crossed the breakpoint)
    if (prevIsMobileRef.current !== newIsMobile) {
      prevIsMobileRef.current = newIsMobile;
      setIsMobile(newIsMobile);
    }
  }, [width]);


  if (tenantType === "CUSTOMER") {
    return null;
  }

  return (
    <>
      {/* GLOBAL_ADMIN / PARTNER => partner + possible customer */}
      <div className={isMobile ? "flex flex-col gap-4" : "flex items-center gap-6"}>
        {tenantType === "GLOBAL_ADMIN" && (
          <div className="flex flex-col">
            <GenericDropdown
              items={partnerOptions}
              value={partnerName}
              onSelect={onSelectPartner}
              loading={isFetching}
              minWidthClass="min-w-[200px]"
              maxWidthClass="max-w-[300px]"
            />
          </div>
        )}
        {(tenantType === "GLOBAL_ADMIN" || tenantType === "PARTNER") && (
          <div className="flex flex-col w-full">
            <ContextSelector
              options={customerOptions}
              value={customerDisplay}
              onSelect={onSelectCustomer}
              disabled={isFetching}
              rightLabel={rightLabel}
              rightId={rightId}
              size="md"
              isPartnerView={rightLabel === "Partner view"}
            />
          </div>
        )}
      </div>
    </>
  );
}
